import { DataFlowConfigurationParsed, FLOW_FALLBACK_DATA, FlowData } from '@/config'
import { DataFlow } from '@coac-gmbh/saifty-main-apis'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import { version } from '../../package.json'
import ISO639_1 from 'iso-639-1'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

export enum PromiseStatuses {
  Pending = 'pending',
  Fulfilled = 'fulfilled',
  Rejected = 'rejected',
}

export function randomBetween(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1) + min)
}

// Get the latest configuration of a flow and parse it to FlowData because the backend returns it as a object that can contain anything
export function getLatestConfiguration(flow: DataFlow): DataFlowConfigurationParsed {
  let data: FlowData

  if (flow.configuration && flow.configuration.length > 0) {
    const latestConfiguration =
      flow.configuration?.reduce((currentLatest, current) => {
        return Number(currentLatest.version ?? 0) > Number(current.version ?? 0) ? currentLatest : current
      }) || {}

    if (latestConfiguration.data) {
      // The saved configuration object can have any key, so we need to cast it to FlowData
      const latestConfigurationData = latestConfiguration.data as { [key: string]: any }
      data = {
        extract: {
          quickExtraction:
            latestConfigurationData.extract?.quickExtraction ?? FLOW_FALLBACK_DATA.extract.quickExtraction,
          allEchaIngredients:
            latestConfigurationData.extract?.allEchaIngredients ?? FLOW_FALLBACK_DATA.extract.allEchaIngredients,
          allEPAIngredients:
            latestConfigurationData.extract?.allEPAIngredients ?? FLOW_FALLBACK_DATA.extract.allEPAIngredients,
          passportId: latestConfigurationData.extract?.passportId ?? FLOW_FALLBACK_DATA.extract.passportId,
          displayAllPassportItems:
            latestConfigurationData.extract?.displayAllPassportItems ??
            FLOW_FALLBACK_DATA.extract.displayAllPassportItems,
          languages: latestConfigurationData.extract?.languages ?? FLOW_FALLBACK_DATA.extract.languages,
          ocr: latestConfigurationData.extract?.ocr ?? FLOW_FALLBACK_DATA.extract.ocr,
        },
        validate: {
          confidence: latestConfigurationData.validate?.confidence ?? FLOW_FALLBACK_DATA.validate.confidence,
          lookup_phrases:
            latestConfigurationData.validate?.lookup_phrases ?? FLOW_FALLBACK_DATA.validate.lookup_phrases,
          echa_reach_call:
            latestConfigurationData.validate?.echa_reach_call ?? FLOW_FALLBACK_DATA.validate.echa_reach_call,
          epa_call: latestConfigurationData.validate?.epa_call ?? FLOW_FALLBACK_DATA.validate.epa_call,
          region: latestConfigurationData.validate?.region ?? FLOW_FALLBACK_DATA.validate.region,
          businessUnit: latestConfigurationData.validate?.businessUnit ?? FLOW_FALLBACK_DATA.validate.businessUnit,
          translate_from_phrases:
            latestConfigurationData.validate?.translate_from_phrases ??
            FLOW_FALLBACK_DATA.validate.translate_from_phrases,
          translate_to_phrases:
            latestConfigurationData.validate?.translate_to_phrases ?? FLOW_FALLBACK_DATA.validate.translate_to_phrases,
          translate_from_document:
            latestConfigurationData.validate?.translate_from_document ??
            FLOW_FALLBACK_DATA.validate.translate_from_document,
          translate_to_document:
            latestConfigurationData.validate?.translate_to_document ??
            FLOW_FALLBACK_DATA.validate.translate_to_document,
        },
        store: {
          dataassetId: latestConfigurationData.store?.dataassetId ?? FLOW_FALLBACK_DATA.store.dataassetId,
          dataassetName: latestConfigurationData.store?.dataassetName ?? FLOW_FALLBACK_DATA.store.dataassetName,
        },
      }
    } else {
      data = FLOW_FALLBACK_DATA
    }

    return {
      version: latestConfiguration.version,
      data,
    }
  } else {
    return {
      version: '1',
      data: FLOW_FALLBACK_DATA,
    }
  }
}

export function sortByDate(a: Date, b: Date, ascSorting: boolean) {
  return ascSorting ? a.getTime() - b.getTime() : b.getTime() - a.getTime()
}

export function sortAlphabetically(a: string, b: string, ascSorting: boolean) {
  if (ascSorting) {
    if (a.toLowerCase() < b.toLowerCase()) {
      return -1
    }
    if (a.toLowerCase() > b.toLowerCase()) {
      return 1
    }
    return 0
  } else {
    if (a.toLowerCase() > b.toLowerCase()) {
      return -1
    }
    if (a.toLowerCase() < b.toLowerCase()) {
      return 1
    }
    return 0
  }
}

export function getFileExtension(file: string) {
  const fileParts = file.split('.')
  return fileParts[fileParts.length - 1]
}

export function arrayRange(start: number, stop: number, step: number) {
  return Array.from({ length: (stop - start) / step + 1 }, (value, index) => start + index * step)
}

export function areEqualObjects(first: object, second: object) {
  return Object.entries(first).sort().toString() === Object.entries(second).sort().toString() // We use 'sort' because the position is important in the comparison and finally, we cast it to string to compare
}

// Formats date to relative time strings (e.g. 3 hours ago).
// Date is a timestamp or iso date without timezone, therefore we assume it's UTC
export function dateToRelativeTimeHumanized(date: string | number, isTimestamp = false) {
  dayjs.extend(relativeTime)
  dayjs.extend(utc)
  const nowInUTC = dayjs.utc()
  if (isTimestamp) date = typeof date === 'number' ? date : Number(date)
  return dayjs.utc(date).from(nowInUTC)
}

export function formatDate(date: string, format?: string) {
  return dayjs(date).format(format ? format : 'DD-MM-YYYY')
}

export function getPromiseStatus(promise: Promise<any>) {
  const t = {}

  return Promise.race([promise, t]).then(
    (v) => (v === t ? PromiseStatuses.Pending : PromiseStatuses.Fulfilled),
    () => PromiseStatuses.Rejected
  )
}

export function isLetterOrNumber(e: KeyboardEvent) {
  if (/^[a-zA-Z0-9 ]*$/.test(e.key)) return true
  else e.preventDefault() // If not match, don't add to input text
}

export function dontAllowWhiteSpaces(e: KeyboardEvent) {
  if (/^\S*$/.test(e.key)) return true
  else e.preventDefault() // If is white space, don't add to input text
}

export function dataAccessor(object: any, path: string) {
  /* 
This function is used to pass and object and a path to search deeply in an object a return the value
- Object Example:
{
  data: {
    name: 'Example'
    value: ' Success'
  },
  status: 'Testing'
}
- Path example: 'data.name'
- Value returned: 'Example'

*/
  const deepAccesors = path.split('.')
  return deepAccesors.reduce(
    (currentValue: any, currentAccesor: any) => (currentValue[currentAccesor] ? currentValue[currentAccesor] : ''),
    object
  )
}

export function getAppVersion() {
  return version
}

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function formatDecimalsOnly(number: number, decimalSeparator: '.' | ',' = '.', maxDecimals = 3): string {
  const roundedNumber = Math.round(number * Math.pow(10, maxDecimals)) / Math.pow(10, maxDecimals)
  return decimalSeparator === '.' ? String(roundedNumber) : String(roundedNumber).replace('.', ',')
}

export function ptValueToCmValue(ptSize: number): number {
  const CM_PER_PT = (1 / 72) * 2.54 // 1pt = 1/72 inches and 1 inch = 2.54 centimeters
  return ptSize * CM_PER_PT
}

export function replaceStringDecimalSeparator(value: string, numberSeparator: '.' | ','): string {
  if (!value) return value

  const numberSeparatorToReplace = numberSeparator === '.' ? ',' : '.'
  const regex = getRegexByNumberSeparator(numberSeparatorToReplace)

  if (
    value.includes(numberSeparatorToReplace) &&
    hasOnlyOneValidSeparator(value, regex) &&
    !hasOnlyOneValidSeparator(value, getRegexByNumberSeparator(numberSeparator))
  ) {
    return value.replace(regex, `$1${numberSeparator}$2`)
  }

  return value
}

function getRegexByNumberSeparator(numberSeparator: '.' | ',') {
  return new RegExp(`(\\d)\\${numberSeparator}(\\d)`, 'g') //regex to get the number separator with numbers between them
}

function hasOnlyOneValidSeparator(value: string, regex: RegExp): boolean {
  const matches = value.match(regex)
  return matches !== null && matches.length === 1
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      const base64String = reader.result as string
      resolve(base64String)
    }

    reader.onerror = (error) => {
      reject(error)
    }

    reader.readAsDataURL(file)
  })
}

export function getLanguageObjectOptionByLanguageCode(languageCode: string) {
  const languageCodeValueInLowerCase = languageCode.toLowerCase()
  let languageCodeToObtainTheFullName = languageCode.toLowerCase()
  const languageCodeNameToShowInLabel = languageCode.toLowerCase()

  if (languageCodeToObtainTheFullName === 'jp') {
    languageCodeToObtainTheFullName = 'ja'
  }

  let languageNameToShow = ISO639_1.getName(languageCodeToObtainTheFullName) || languageCodeToObtainTheFullName

  if (languageCodeToObtainTheFullName === 'tl') {
    languageNameToShow = 'Philipines'
  }

  return {
    name: `${languageNameToShow}: ${languageCodeNameToShowInLabel}`,
    value: languageCodeValueInLowerCase,
  }
}

export async function urlToFile(url: string, filename: string): Promise<File> {
  const response = await fetch(url)
  const blob = await response.blob()

  return new File([blob], filename, { type: blob.type })
}

export function base64ToFile(base64: string, fileName: string, mimeType?: string): File {
  const arr = base64.split(',')
  const mime = mimeType || arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream'
  const bstr = window.Buffer.from(arr[arr.length - 1], 'base64')
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr[n]
  }

  return new File([u8arr], fileName, { type: mime })
}

export function hasCanonicalSuffix(value: string) {
  if (!value) return
  const parts = value.split('_')
  if (parts.length < 2) return
  const lastPart = parts[parts.length - 1]

  return isNumeric(lastPart)
}

export function isNumeric(str: string) {
  return /^[0-9]+$/.test(str)
}

export function markdownToSafeHtml(markdown: string) {
  const dirtyHtml = marked.parse(markdown || '')
  if (typeof dirtyHtml === 'string') {
    const cleanHtml = DOMPurify.sanitize(dirtyHtml)
    return cleanHtml
  }

  return ''
}

export const IS_PRODUCTION =
  import.meta.env.PROD ||
  import.meta.env.NODE_ENV === 'production' ||
  ('ENVIRONMENT' in window.ENV && (window.ENV.ENVIRONMENT === 'production' || window.ENV.ENVIRONMENT === 'PROD'))

export async function objectUrlToBlob(url: string): Promise<Blob> {
  const response = await fetch(url)
  return await response.blob()
}
